import { useCallback, useEffect, useState } from "react";
import { MonitorSmartphone, RefreshCw, LogOut } from "lucide-react";
import { api } from "@/lib/api";

function formatDate(value) {
  if (!value) return "Unknown";
  return new Date(value).toLocaleString("en-GB");
}

function shortSessionId(value) {
  if (!value) return "Unknown";
  return `${value.slice(0, 8)}...${value.slice(-6)}`;
}

export default function AdminSessions() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busySession, setBusySession] = useState(null);
  const [revokingOthers, setRevokingOthers] = useState(false);
  const [message, setMessage] = useState("");

  const loadSessions = useCallback(async () => {
    setLoading(true);

    try {
      const { data } = await api.get("/auth/sessions");
      setSessions(data || []);
    } catch {
      setSessions([]);
      setMessage("Unable to load active sessions.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const revokeSession = async (sid) => {
    if (!window.confirm("Revoke this session?")) return;

    setBusySession(sid);
    setMessage("");

    try {
      await api.delete(`/auth/sessions/${sid}`);
      setMessage("Session revoked.");
      await loadSessions();
    } catch (error) {
      setMessage(
        error?.response?.data?.detail || "Unable to revoke this session."
      );
    } finally {
      setBusySession(null);
    }
  };

  const revokeOthers = async () => {
    if (!window.confirm("Revoke all other sessions?")) return;

    setRevokingOthers(true);
    setMessage("");

    try {
      const { data } = await api.post("/auth/sessions/revoke-others");
      const count = data?.revoked_count ?? 0;
      setMessage(`${count} other session${count === 1 ? "" : "s"} revoked.`);
      await loadSessions();
    } catch (error) {
      setMessage(
        error?.response?.data?.detail || "Unable to revoke other sessions."
      );
    } finally {
      setRevokingOthers(false);
    }
  };

  const otherSessionCount = sessions.filter(
    (session) => !session.is_current
  ).length;

  return (
    <div className="max-w-5xl" data-testid="page-admin-security-sessions">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="rounded-sm bg-navy p-2.5 text-cyan">
            <MonitorSmartphone size={22} />
          </div>

          <div>
            <h1 className="font-heading text-2xl font-bold text-navy">
              Active Sessions
            </h1>
            <p className="mt-1 text-sm text-slate">
              Review and revoke authenticated admin sessions.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={revokeOthers}
          disabled={revokingOthers || otherSessionCount === 0}
          className="inline-flex items-center gap-2 rounded-sm bg-navy px-4 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
        >
          <LogOut size={16} />
          {revokingOthers ? "Revoking..." : "Revoke all other sessions"}
        </button>
      </div>

      {message && (
        <div className="mt-6 rounded-sm border border-brandborder bg-white px-4 py-3 text-sm text-navy">
          {message}
        </div>
      )}

      <section className="mt-8 rounded-md border border-brandborder bg-white p-6">
        <div className="flex items-center justify-between gap-4">
          <div className="text-sm text-slate">
            {sessions.length} active session{sessions.length === 1 ? "" : "s"}
          </div>

          <button
            type="button"
            onClick={loadSessions}
            disabled={loading}
            className="inline-flex items-center gap-2 text-sm font-semibold text-navy disabled:opacity-50"
          >
            <RefreshCw size={15} />
            Refresh
          </button>
        </div>

        <div className="mt-5 space-y-3">
          {loading ? (
            <div className="text-sm text-slate">Loading sessions...</div>
          ) : sessions.length === 0 ? (
            <div className="text-sm text-slate">No active sessions found.</div>
          ) : (
            sessions.map((session) => (
              <div
                key={session.sid}
                className="rounded-sm border border-brandborder bg-lightbg p-4"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="font-semibold text-navy">
                        {session.is_current ? "Current session" : "Admin session"}
                      </div>

                      {session.is_current && (
                        <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                          This session
                        </span>
                      )}
                    </div>

                    <div className="mt-3 grid gap-1 text-xs text-slate">
                      <div>
                        <span className="font-semibold text-navy">Session:</span>{" "}
                        {shortSessionId(session.sid)}
                      </div>
                      <div>
                        <span className="font-semibold text-navy">Created:</span>{" "}
                        {formatDate(session.created_at)}
                      </div>
                      <div>
                        <span className="font-semibold text-navy">
                          Last activity:
                        </span>{" "}
                        {formatDate(session.last_seen_at)}
                      </div>
                      <div>
                        <span className="font-semibold text-navy">
                          Maximum expiry:
                        </span>{" "}
                        {formatDate(session.absolute_expires_at)}
                      </div>
                    </div>
                  </div>

                  {!session.is_current && (
                    <button
                      type="button"
                      onClick={() => revokeSession(session.sid)}
                      disabled={busySession === session.sid}
                      className="rounded-sm border border-brandborder bg-white px-3 py-2 text-sm font-semibold text-navy hover:bg-slate-50 disabled:opacity-50"
                    >
                      {busySession === session.sid
                        ? "Revoking..."
                        : "Revoke session"}
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
